import React, { useState, useEffect } from 'react';
import { Users, PlusCircle, Trash2, AlertCircle, Heart, DollarSign, HelpCircle, Star, FileText, Shield } from 'react-feather';
import { getTaxConstants } from '../taxConstants';

// --- Centralized list of relationship options ---
const RELATIONSHIP_OPTIONS = [
  'Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild',
  'Parent', 'Grandparent', 'Niece', 'Nephew', 'Aunt', 'Uncle', 'Other'
];

// --- Define which relationships are considered a "child" for UI purposes ---
const CHILD_RELATIONSHIPS = ['Son', 'Daughter', 'Foster Child', 'Brother', 'Sister', 'Grandchild', 'Niece', 'Nephew'];

// --- Qualification logic ---
const checkQualifications = (dependent, taxYear) => {
  const qualifications = {
    isQualifyingChildForCTC: false,
    isQualifyingRelativeForODC: false,
    isQualifyingPersonForCare: false,
  };

  if (!dependent || !dependent.dob || !dependent.relationship) {
    return qualifications;
  }

  const birthDate = new Date(dependent.dob);
  const age = taxYear - birthDate.getFullYear();

  const isChildRelationship = CHILD_RELATIONSHIPS.includes(dependent.relationship);
  if (isChildRelationship && age < 17) {
    qualifications.isQualifyingChildForCTC = true;
  }

  if (!qualifications.isQualifyingChildForCTC) {
    const meetsRelationshipTest = dependent.livedWithYou || RELATIONSHIP_OPTIONS.includes(dependent.relationship);
    const meetsGrossIncomeTest = dependent.grossIncome < 4700;
    const meetsSupportTest = dependent.providedHalfSupport;

    if (meetsRelationshipTest && meetsGrossIncomeTest && meetsSupportTest) {
      qualifications.isQualifyingRelativeForODC = true;
    }
  }

  if (age < 13 || dependent.isDisabled) {
    qualifications.isQualifyingPersonForCare = true;
  }

  return qualifications;
};

// --- Helper function to look up the decimal rate based on AGI ---
const getPriorYearDecimal = (agi) => {
  if (agi <= 15000) return 0.35;
  if (agi <= 17000) return 0.34;
  if (agi <= 19000) return 0.33;
  if (agi <= 21000) return 0.32;
  if (agi <= 23000) return 0.31;
  if (agi <= 25000) return 0.30;
  if (agi <= 27000) return 0.29;
  if (agi <= 29000) return 0.28;
  if (agi <= 31000) return 0.27;
  if (agi <= 33000) return 0.26;
  if (agi <= 35000) return 0.25;
  if (agi <= 37000) return 0.24;
  if (agi <= 39000) return 0.23;
  if (agi <= 41000) return 0.22;
  if (agi <= 43000) return 0.21;
  return 0.20; // Over 43,000
};


function DependentsForm({ formData, onDependentsUpdate, onProvidersUpdate, onDependentCareBenefitsUpdate, onDataChange, taxYear, adoptionWorksheet }) {
  const { dependents = [], filingStatus, dependentCare = {}, careProviders = [], dependentCareBenefits = [], adoptionCredit = {} } = formData;
  const [ssns, setSsns] = useState({});

  const constants = getTaxConstants(taxYear);
  const limits = constants.LIMITS;

  // --- HANDLERS ---

  const handleBenefitsRadioChange = (e) => {
    // Convert the string value from the radio button ("true" or "false") into a real boolean
    const booleanValue = e.target.value === 'true';

    const syntheticEvent = {
      target: {
        name: e.target.name,
        value: booleanValue,
        type: 'radio' // Set type to avoid checkbox logic in the main handler
      }
    };
    onDataChange(syntheticEvent);
  };

  const handleDependentsChange = (id, field, value) => {
    const newDependents = dependents.map(dep => {
      if (dep.id === id) {
        return { ...dep, [field]: value };
      }
      return dep;
    });
    onDependentsUpdate(newDependents);
  };

  const handleAddDependent = () => {
    const newDependent = {
      id: `dep-${Date.now()}`,
      firstName: '', lastName: '', relationship: 'Daughter', dob: '',
      grossIncome: 0,
      livedWithYou: false,
      providedHalfSupport: false,
      isDisabled: false,
      hasValidTIN: false,
      isUSCitizen: false,
      wasAdopted: false,
      isForeignChild: false,            // ADDED
      adoptionFinalizedInYear: false,
      qualifiedCareExpenses: 0,
    };
    onDependentsUpdate([...dependents, newDependent]);
  };

  const handleDeleteDependent = (id) => {
    onDependentsUpdate(dependents.filter(dep => dep.id !== id));
    const newSsns = { ...ssns };
    delete newSsns[id];
    setSsns(newSsns);
  };
  
  const handleSsnChange = (id, value) => {
    const formattedValue = value.replace(/[^0-9]/g, '').replace(/^(\d{3})(\d{2})(\d{4})$/, '$1-$2-$3');
    setSsns(prevSsns => ({ ...prevSsns, [id]: formattedValue.substring(0, 11) }));
  };

  const handleAddProvider = () => {
    const newProvider = {
      id: `prov-${Date.now()}`, name: '',
      address: { street: '', city: '', state: '', zip: '' },
      tin: '', isEmployee: false, amountPaid: 0,
    };
    onProvidersUpdate([...careProviders, newProvider]);
  };

  const handleDeleteProvider = (id) => {
    onProvidersUpdate(careProviders.filter(p => p.id !== id));
  };

  const handleProviderChange = (index, field, value) => {
    const newProviders = JSON.parse(JSON.stringify(careProviders));
    const providerToUpdate = newProviders[index];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      if (!providerToUpdate[parent]) providerToUpdate[parent] = {};
      providerToUpdate[parent][child] = value;
    } else {
      providerToUpdate[field] = value;
    }
    onProvidersUpdate(newProviders);
  };

  const handleAddBenefit = () => {
    const newBenefit = { id: `benefit-${Date.now()}`, description: 'From Employer (W-2 Box 10)', amount: 0 };
    onDependentCareBenefitsUpdate([...dependentCareBenefits, newBenefit]);
  };

  const handleDeleteBenefit = (id) => {
    onDependentCareBenefitsUpdate(dependentCareBenefits.filter(b => b.id !== id));
  };

  const handleBenefitChange = (index, field, value) => {
    const newBenefits = [...dependentCareBenefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    onDependentCareBenefitsUpdate(newBenefits);
  };

  const handlePriorYearExpenseChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    const priorYearLimit = dependentCare.priorYearQualifyingPersonsCount >= 2 ? 6000 : (dependentCare.priorYearQualifyingPersonsCount === 1 ? 3000 : 0);
    const cappedValue = Math.min(value, priorYearLimit);
    onDataChange({ target: { name: 'dependentCare.priorYearExpensesPaidInCurrentYear', value: cappedValue } });
  };
  
  // --- DERIVED STATE & EFFECTS ---

  const hasQualifyingPersonsForCare = dependents.some(dep => checkQualifications(dep, taxYear).isQualifyingPersonForCare);
  const showCareProvidersSection = hasQualifyingPersonsForCare;
  const qualifyingPersons = dependents.filter(dep => checkQualifications(dep, taxYear).isQualifyingPersonForCare);
  const adoptedQualifyingDependents = dependents.filter(dep => {
    if (!dep.wasAdopted || !dep.dob) return false;
    const age = taxYear - new Date(dep.dob).getFullYear();
    return age < 18 || dep.isDisabled;
  });

  // --- COMPLETE USEEFFECT FOR ALL ADOPTION CREDIT CALCULATIONS ---
  useEffect(() => {
    // --- Part 1: Per-Child Expense and Benefit Calculations ---
    const newFinalAmounts = {};
    const newPerChildLimitations = {};
    const newMinBenefitComparisons = {};
    const newTaxablePriorYearAmounts = {};
    const newTaxableCurrentYearAmounts = {};
    const maxExclusion = limits.ADOPTION_CREDIT_LIMIT;

    // Calculate the total of all current year benefits entered
    const totalCurrentBenefits = Object.values(adoptionCredit.benefitsByChild || {}).reduce(
      (sum, child) => sum + (parseFloat(child.currentYear) || 0),
      0
    );

    // Loop through each child to calculate their individual benefit limits
    for (const childId in adoptionCredit.benefitsByChild) {
      const childBenefits = adoptionCredit.benefitsByChild[childId];
      if (childBenefits) {
        const currentYearAmount = parseFloat(childBenefits.currentYear) || 0;
        const taxableCurrentAmount = Math.max(0, currentYearAmount - maxExclusion);
        newTaxableCurrentYearAmounts[childId] = taxableCurrentAmount;

        let taxablePriorAmount = 0;
        if (childBenefits.receivedPriorYear) {
          const priorYearAmount = parseFloat(childBenefits.priorYear) || 0;
          taxablePriorAmount = Math.max(0, priorYearAmount - maxExclusion);
          newTaxablePriorYearAmounts[childId] = taxablePriorAmount;
        }

        const dependent = adoptedQualifyingDependents.find(d => d.id === childId);
        let minComparison;

        if (dependent && dependent.isDisabled && dependent.adoptionFinalizedInYear) {
          minComparison = taxablePriorAmount;
        } else {
          minComparison = Math.min(currentYearAmount, taxablePriorAmount);
        }
        newMinBenefitComparisons[childId] = minComparison;
      }
    }

    // --- Part 2: MAGI Phase-Out and Final Per-Child Calculations ---
    const magi = parseFloat(adoptionCredit.modifiedAGI) || 0;
    const phaseOutStart = limits.ADOPTION_PHASE_OUT_START;
    let reductionFactor = 0;
    if (magi > phaseOutStart) {
      reductionFactor = (magi - phaseOutStart) / 40000;
    }
    if (reductionFactor > 1) reductionFactor = 1;

    adoptedQualifyingDependents.forEach(dep => {
      const childExpense = parseFloat(adoptionCredit.expensesByChild?.[dep.id]) || 0;
      const magiAdjustedExpense = childExpense * (1 - reductionFactor);
      const benefitComparisonLimit = newMinBenefitComparisons[dep.id] || 0;
      const finalAmount = magiAdjustedExpense - benefitComparisonLimit;
      newFinalAmounts[dep.id] = Math.max(0, finalAmount);

      const newLimitation = magiAdjustedExpense - finalAmount;
      newPerChildLimitations[dep.id] = newLimitation;
    });

    // --- Part 3: Final Totals and Comparisons ---
    const totalLimitation = Object.values(newPerChildLimitations).reduce(
      (sum, amount) => sum + amount,
      0
    );

    const isGreater = totalLimitation > totalCurrentBenefits;
    let finalCalculationResult;

    if (isGreater) {
      finalCalculationResult = totalLimitation - totalCurrentBenefits;
    } else {
      finalCalculationResult = totalCurrentBenefits - totalLimitation;
    }

    // --- Part 4: Update State ---
    // This section checks each calculated value against the current state and only
    // calls the update function if a change is detected, preventing infinite loops.
    if (totalCurrentBenefits !== adoptionCredit.totalCurrentYearBenefits) {
      onDataChange({ target: { name: 'adoptionCredit.totalCurrentYearBenefits', value: totalCurrentBenefits } });
    }
    if (JSON.stringify(newTaxableCurrentYearAmounts) !== JSON.stringify(adoptionCredit.taxableCurrentYearBenefitsByChild)) {
      onDataChange({ target: { name: 'adoptionCredit.taxableCurrentYearBenefitsByChild', value: newTaxableCurrentYearAmounts } });
    }
    if (JSON.stringify(newTaxablePriorYearAmounts) !== JSON.stringify(adoptionCredit.taxablePriorYearBenefitsByChild)) {
      onDataChange({ target: { name: 'adoptionCredit.taxablePriorYearBenefitsByChild', value: newTaxablePriorYearAmounts } });
    }
    if (JSON.stringify(newMinBenefitComparisons) !== JSON.stringify(adoptionCredit.minBenefitComparisonByChild)) {
      onDataChange({ target: { name: 'adoptionCredit.minBenefitComparisonByChild', value: newMinBenefitComparisons } });
    }
    if (JSON.stringify(newFinalAmounts) !== JSON.stringify(adoptionCredit.finalChildCreditOrCarryforward)) {
      onDataChange({ target: { name: 'adoptionCredit.finalChildCreditOrCarryforward', value: newFinalAmounts } });
    }
    if (JSON.stringify(newPerChildLimitations) !== JSON.stringify(adoptionCredit.perChildCreditLimitation)) {
      onDataChange({ target: { name: 'adoptionCredit.perChildCreditLimitation', value: newPerChildLimitations } });
    }
    if (totalLimitation !== adoptionCredit.totalPerChildCreditLimitation) {
      onDataChange({ target: { name: 'adoptionCredit.totalPerChildCreditLimitation', value: totalLimitation } });
    }
    if (isGreater !== adoptionCredit.isLimitationGreaterThanBenefits) {
      onDataChange({ target: { name: 'adoptionCredit.isLimitationGreaterThanBenefits', value: isGreater } });
    }
    if (finalCalculationResult !== adoptionCredit.finalBenefitCalculation) {
      onDataChange({ target: { name: 'adoptionCredit.finalBenefitCalculation', value: finalCalculationResult } });
    }

  }, [
    dependents,
    adoptedQualifyingDependents,
    adoptionCredit.modifiedAGI,
    adoptionCredit.expensesByChild,
    adoptionCredit.benefitsByChild,
    adoptionCredit.minBenefitComparisonByChild,
    adoptionCredit.taxableCurrentYearBenefitsByChild,
    adoptionCredit.taxablePriorYearBenefitsByChild,
    adoptionCredit.finalChildCreditOrCarryforward,
    adoptionCredit.perChildCreditLimitation,
    adoptionCredit.totalCurrentYearBenefits,
    adoptionCredit.totalPerChildCreditLimitation,
    adoptionCredit.isLimitationGreaterThanBenefits,
    adoptionCredit.finalBenefitCalculation,
    onDataChange,
    limits
  ]);

   useEffect(() => {
  const numberOfQualifyingChildren = adoptedQualifyingDependents.length;
  const calculatedAmount = numberOfQualifyingChildren * limits.ADOPTION_CREDIT_LIMIT;
  
  // Calculate total expenses from all children
  const totalExpenses = adoptedQualifyingDependents.reduce((sum, dep) => {
    return sum + (parseFloat(adoptionCredit.expensesByChild?.[dep.id]) || 0);
  }, 0);
  
  // Update line2 if needed
  if (calculatedAmount !== adoptionCredit.form8839_year2024_line2) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line2', 
        value: calculatedAmount 
      } 
    });
  }
  
  // Update line5 (total expenses) if needed
  if (totalExpenses !== adoptionCredit.form8839_year2024_line5) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line5', 
        value: totalExpenses 
      } 
    });
  }
  
  // Calculate line4
  const newLine4 = adoptionCredit.form8839_year2024_line3 - adoptionCredit.form8839_year2024_line2;
  if (newLine4 !== adoptionCredit.form8839_year2024_line4) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line4', 
        value: newLine4 
      } 
    });
  }
  
  // Calculate line6
  const newLine6 = Math.min(adoptionCredit.form8839_year2024_line4, adoptionCredit.form8839_year2024_line5);
  if (newLine6 !== adoptionCredit.form8839_year2024_line6) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line6', 
        value: newLine6 
      } 
    });
  }

  let newLine7;

  const newLine8 = adoptionCredit.form8839_year2024_line7 - limits.ADOPTION_PHASE_OUT_START;
  const newLine9 = Math.max(1.000, parseFloat((newLine8 / 40000).toFixed(3)));

  let newLine12;
  
  adoptedQualifyingDependents.forEach(dep => {
    const childExpense = parseFloat(adoptionCredit.expensesByChild?.[dep.id]) || 0;
    const adjustedExpense = childExpense * newLine9;
    const lineElevenAmount = adjustedExpense - newLine6;
    if (adjustedExpense !== adoptionCredit.adjustedExpensesByChild?.[dep.id]) {
      onDataChange({
        target: {
          name: `adoptionCredit.adjustedExpensesByChild.${dep.id}`,
          value: adjustedExpense
        }
      });
    }
    if (lineElevenAmount !== adoptionCredit.lineEleven?.[dep.id]) {
      onDataChange({
        target: {
          name: `adoptionCredit.lineEleven.${dep.id}`,
          value: lineElevenAmount
        }
      });
    }
    newLine12 += lineElevenAmount;
  })

  if (newLine7 !== adoptionCredit.form8839_year2024_line7) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line7', 
        value: newLine7 
      } 
    });
  }

  if (newLine8 !== adoptionCredit.form8839_year2024_line8) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line8', 
        value: newLine8
      } 
    });
  }

  if (newLine9 !== adoptionCredit.form8839_year2024_line9) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line9', 
        value: newLine9 
      } 
    });
  }

  if (newLine12 !== adoptionCredit.form8839_year2024_line12) {
    onDataChange({ 
      target: { 
        name: 'adoptionCredit.form8839_year2024_line12', 
        value: newLine12
      } 
    });
  }

  let newLine14 = 0;

  if (newLine14 !== (adoptionCredit.totalCarryforward + newLine12)) {
    onDataChange({
      target: {
        name: 'adoptionCredit.form8839_year2024_line14',
        value: newLine14
      }
    })
  }

  const newLine16 = Math.min(adoptionCredit.form8839_year2024_line15, adoptionCredit.form8839_year2024_line14)
  
}, [
  adoptedQualifyingDependents,
  adoptionCredit.expensesByChild, // Now depends on the expenses object
  adoptionCredit.form8839_year2024_line2,
  adoptionCredit.form8839_year2024_line3,
  adoptionCredit.form8839_year2024_line4,
  adoptionCredit.form8839_year2024_line5,
  adoptionCredit.form8839_year2024_line6,
  adoptionCredit.form8839_year2024_line7,
  adoptionCredit.form8839_year2024_line8,
  adoptionCredit.form8839_year2024_line9,
  adoptionCredit.form8839_year2024_line12,
  adoptionCredit.totalCarryforward,
  adoptionCredit.form8839_year2024_line14,
  adoptionCredit.form8839_year2024_line15,
  adoptionCredit.form8839_year2024_line16,
  onDataChange,
  limits
]);

  useEffect(() => {
  if (adoptionCredit.hasCarryforward) {
    // Line 1: Current year credit
    const line1 = parseFloat(adoptionCredit.carryforward_line1) || 0;

    if (!adoptionCredit.usedWorksheetIn2023) {
      // Simple case: no prior worksheet
      if (line1 !== adoptionCredit.totalCarryforward) {
        onDataChange({
          target: {
            name: 'adoptionCredit.totalCarryforward',
            value: line1
          }
        });
      }
      return;
    }

    // Lines 2-6: Individual year carryforwards
    const line2 = parseFloat(adoptionCredit.carryforward_2019) || 0;
    const line3 = parseFloat(adoptionCredit.carryforward_2020) || 0;
    const line4 = parseFloat(adoptionCredit.carryforward_2021) || 0;
    const line5 = parseFloat(adoptionCredit.carryforward_2022) || 0;
    const line6 = parseFloat(adoptionCredit.carryforward_2023) || 0;

    // Line 7: Total including all carryforwards
    const line7 = line1 + line2 + line3 + line4 + line5 + line6;

    // Line 8: Credit used this year
    const line8 = parseFloat(adoptionCredit.carryforward_line16) || 0;

    // Line 9: Remaining after using credit
    const line9 = line7 - line8;

    // Check if any prior year amounts exist
    const hasPriorYears = line2 > 0 || line3 > 0 || line4 > 0 || line5 > 0 || line6 > 0;

    if (!hasPriorYears) {
      if (line9 !== adoptionCredit.totalCarryforward) {
        onDataChange({
          target: {
            name: 'adoptionCredit.totalCarryforward',
            value: line9
          }
        });
      }
      return;
    }

    // Complex calculation - apply credit usage in chronological order (FIFO)
    let remaining = line8;

    // Line 10: 2019 credit remaining (oldest, used first)
    const line10 = Math.max(0, line2 - remaining);
    remaining = Math.max(0, remaining - line2);

    // Lines 11-12: 2020 credit
    const line12 = Math.max(0, line3 - remaining);
    remaining = Math.max(0, remaining - line3);

    // Lines 13-14: 2021 credit
    const line14 = Math.max(0, line4 - remaining);
    remaining = Math.max(0, remaining - line4);

    // Lines 15-16: 2022 credit
    const line16 = Math.max(0, line5 - remaining);
    remaining = Math.max(0, remaining - line5);

    // Lines 17-18: 2023 credit
    const line18 = Math.max(0, line6 - remaining);

    // Line 19: Total prior years carried forward
    const line19 = line10 + line12 + line14 + line16 + line18;

    // Line 20: 2024 credit carryforward
    const line20 = Math.max(0, line9 - line19);

    // Line 23: Total credit carryforward to 2025
    const line23 = line19 + line20 - line10;

    if (line23 !== adoptionCredit.totalCarryforward) {
      onDataChange({
        target: {
          name: 'adoptionCredit.totalCarryforward',
          value: line23
        }
      });
    }
  } else {
    // Reset if checkbox is unchecked
    if (adoptionCredit.totalCarryforward > 0) {
      onDataChange({
        target: {
          name: 'adoptionCredit.totalCarryforward',
          value: 0
        }
      });
    }
  }
}, [
  adoptionCredit.hasCarryforward,
  adoptionCredit.usedWorksheetIn2023,
  adoptionCredit.carryforward_line1,
  adoptionCredit.carryforward_2019,
  adoptionCredit.carryforward_2020,
  adoptionCredit.carryforward_2021,
  adoptionCredit.carryforward_2022,
  adoptionCredit.carryforward_2023,
  adoptionCredit.carryforward_line16,
  adoptionCredit.totalCarryforward,
  onDataChange
]);

  const handleAdoptionBenefitChange = (childId, field, value) => {
    // This creates a dynamic name string like "adoptionCredit.benefitsByChild.dep-12345.currentYear"
    const name = `adoptionCredit.benefitsByChild.${childId}.${field}`;
    
    // The main onDataChange handler in App.js is smart enough to handle nested state
    // when given a name string with dots.
    const syntheticEvent = {
      target: {
        name: name,
        value: value,
        // Help the main handler distinguish between checkboxes and other inputs
        type: field === 'receivedPriorYear' ? 'checkbox' : 'number'
      }
    };
    onDataChange(syntheticEvent);
  };

  useEffect(() => {
    const newTaxablePriorYearAmounts = {};
    const newTaxableCurrentYearAmounts = {}; // Added for current year
    const newMinBenefitComparisons = {};
    const maxExclusion = limits.ADOPTION_CREDIT_LIMIT; 

    const totalCurrentBenefits = Object.values(adoptionCredit.benefitsByChild || {}).reduce(
      (sum, child) => sum + (parseFloat(child.currentYear) || 0),
      0
    );

    for (const childId in adoptionCredit.benefitsByChild) {
      const childBenefits = adoptionCredit.benefitsByChild[childId];
      if (childBenefits) {
        // Calculate for Current Year
        const currentYearAmount = parseFloat(childBenefits.currentYear) || 0;
        const taxableCurrentAmount = Math.max(0, currentYearAmount - maxExclusion);
        newTaxableCurrentYearAmounts[childId] = taxableCurrentAmount;

        let taxablePriorAmount = 0; // <-- FIX: Declare with a default value here
        if (childBenefits.receivedPriorYear) {
          const priorYearAmount = parseFloat(childBenefits.priorYear) || 0;
          taxablePriorAmount = Math.max(0, priorYearAmount - maxExclusion);
        
          newTaxablePriorYearAmounts[childId] = taxablePriorAmount;
        }

        const dependent = adoptedQualifyingDependents.find(d => d.id === childId);
        let minComparison;

        // Check for the special needs + finalized adoption condition
        if (dependent && dependent.isDisabled && dependent.adoptionFinalizedInYear) {
          // If met, the value is ONLY the taxable portion of prior year benefits
          minComparison = taxablePriorAmount;
        } else {
          // Otherwise, perform the standard comparison
          minComparison = Math.min(currentYearAmount, taxablePriorAmount);
        }
        newMinBenefitComparisons[childId] = minComparison;
      }
    }

    if (totalCurrentBenefits !== adoptionCredit.totalCurrentYearBenefits) {
      onDataChange({
        target: {
          name: 'adoptionCredit.totalCurrentYearBenefits',
          value: totalCurrentBenefits
        }
      });
    }

    // Update state for current year if changed
    if (JSON.stringify(newTaxableCurrentYearAmounts) !== JSON.stringify(adoptionCredit.taxableCurrentYearBenefitsByChild)) {
      onDataChange({
        target: {
          name: 'adoptionCredit.taxableCurrentYearBenefitsByChild',
          value: newTaxableCurrentYearAmounts
        }
      });
    }

    // Update state for prior year if changed
    if (JSON.stringify(newTaxablePriorYearAmounts) !== JSON.stringify(adoptionCredit.taxablePriorYearBenefitsByChild)) {
      onDataChange({
        target: {
          name: 'adoptionCredit.taxablePriorYearBenefitsByChild',
          value: newTaxablePriorYearAmounts
        }
      });
    }

    if (JSON.stringify(newMinBenefitComparisons) !== JSON.stringify(adoptionCredit.minBenefitComparisonByChild)) {
      onDataChange({
        target: {
          name: 'adoptionCredit.minBenefitComparisonByChild',
          value: newMinBenefitComparisons
        }
      });
    }
  }, [dependents, adoptionCredit.benefitsByChild, adoptionCredit.taxablePriorYearBenefitsByChild, adoptionCredit.taxableCurrentYearBenefitsByChild, adoptionCredit.totalCurrentYearBenefits, adoptionCredit.minBenefitComparisonByChild, onDataChange, limits]);
  
  useEffect(() => {
     if (dependentCare.paidForPriorYearCare) {
        const limit1 = limits.DEPENDENT_CARE_LIMIT_1;
        const limit2 = limits.DEPENDENT_CARE_LIMIT_2;
        const priorYearLimit = dependentCare.priorYearQualifyingPersonsCount >= 2 ? limit2 : (dependentCare.priorYearQualifyingPersonsCount === 1 ? limit1 : 0);
        if (dependentCare.priorYearExpensesPaidInCurrentYear > priorYearLimit) {
            onDataChange({ target: { name: 'dependentCare.priorYearExpensesPaidInCurrentYear', value: priorYearLimit }});
        }
     }
  }, [dependentCare.priorYearQualifyingPersonsCount, dependentCare.paidForPriorYearCare, dependentCare.priorYearExpensesPaidInCurrentYear, onDataChange, limits]);

  useEffect(() => {
    if (showCareProvidersSection) {
      const totalCalculatedExpenses = qualifyingPersons.reduce((sum, person) => sum + (parseFloat(person.qualifiedCareExpenses) || 0), 0);
      const limit1 = limits.DEPENDENT_CARE_LIMIT_1;
      const limit2 = limits.DEPENDENT_CARE_LIMIT_2;
      const expenseLimit = qualifyingPersons.length >= 2 ? limit2 : limit1;
      const finalTotal = Math.min(totalCalculatedExpenses, expenseLimit);
      if (finalTotal !== dependentCare.totalExpenses) {
        onDataChange({ target: { name: 'dependentCare.totalExpenses', value: finalTotal } });
      }
    }
  }, [dependents, dependentCare.receivedBenefits, qualifyingPersons, onDataChange, showCareProvidersSection, dependentCare.totalExpenses, limits]);
  
  useEffect(() => {
    if (dependentCare.paidForPriorYearCare) {
      // Step 1: Calculate Worksheet A, Line 9 (Prior Year Earned Income Limit)
      let worksheetA9 = dependentCare.priorYearUserEarnedIncome || 0;
      if (filingStatus.includes('Married')) {
        worksheetA9 = Math.min(dependentCare.priorYearUserEarnedIncome || 0, dependentCare.priorYearSpouseEarnedIncome || 0);
      }

      // Step 2: Calculate the Reduced Expense Limit for the Prior Year
      const limit1 = limits.DEPENDENT_CARE_LIMIT_1;
      const limit2 = limits.DEPENDENT_CARE_LIMIT_2;
      const priorYearBaseLimit = dependentCare.priorYearQualifyingPersonsCount >= 2 ? limit2 : (dependentCare.priorYearQualifyingPersonsCount === 1 ? limit1 : 0);
      const priorYearReducedLimit = Math.max(0, priorYearBaseLimit - (dependentCare.priorYearBenefitsReceivedLessExclusions || 0));

      // Step 3: Calculate Worksheet A, Line 10 (Credit Base Amount)
      const worksheetA10 = Math.min(
        worksheetA9,
        priorYearReducedLimit,
        dependentCare.priorYearExpensesPaidInCurrentYear || 0
      );

      // Step 4: Determine Decimal and Final Credit (Form 2441, Line 9b)
      let prevYearDec = 0;
      let form2441_line9b = 0;
      if (worksheetA10 > 0) {
        prevYearDec = getPriorYearDecimal(dependentCare.priorYearAGI || 0);
        form2441_line9b = worksheetA10 * prevYearDec;
      }

      // Step 5: Update state with all new values
      if (worksheetA10 !== dependentCare.worksheetA10) onDataChange({ target: { name: 'dependentCare.worksheetA10', value: worksheetA10 }});
      if (prevYearDec !== dependentCare.prevYearDec) onDataChange({ target: { name: 'dependentCare.prevYearDec', value: prevYearDec }});
      if (form2441_line9b !== dependentCare.form2441_line9b) onDataChange({ target: { name: 'dependentCare.form2441_line9b', value: form2441_line9b }});
    } else {
        // Reset values if the main checkbox is unchecked
        if (dependentCare.worksheetA10 > 0) onDataChange({ target: { name: 'dependentCare.worksheetA10', value: 0 }});
        if (dependentCare.form2441_line9b > 0) onDataChange({ target: { name: 'dependentCare.form2441_line9b', value: 0 }});
    }
  }, [
    dependentCare.paidForPriorYearCare,
    dependentCare.priorYearQualifyingPersonsCount,
    dependentCare.priorYearBenefitsReceivedLessExclusions,
    dependentCare.priorYearUserEarnedIncome,
    dependentCare.priorYearSpouseEarnedIncome,
    dependentCare.priorYearExpensesPaidInCurrentYear,
    dependentCare.priorYearAGI,
    filingStatus,
    onDataChange,
    dependentCare.worksheetA10,
    dependentCare.prevYearDec,
    dependentCare.form2441_line9b,
    limits
  ]);

  useEffect(() => {
    if (dependentCare.receivedBenefits) {
      // 1. Calculate Line 15: Adjusted Benefits
      const totalBenefits = dependentCareBenefits.reduce((sum, benefit) => sum + (parseFloat(benefit.amount) || 0), 0);
      const line15 = (totalBenefits + (dependentCare.priorYearCarryover || 0)) - (dependentCare.forfeitedOrCarriedOverAmount || 0);

      // 2. Calculate Line 17: Smaller of adjusted benefits or total expenses
      const line17 = Math.min(line15, parseFloat(dependentCare.form2441_line16) || 0);
      
      // 3. Get Earned Income values from user inputs
      const line18 = parseFloat(dependentCare.form2441_line18) || 0;
      const spouseIncomeInput = parseFloat(dependentCare.form2441_line19) || 0;

      // 4. Determine Line 19 value based on filing status
      const line19 = filingStatus.includes('Married') ? spouseIncomeInput : line18;

      // 5. Calculate Line 20: Smallest of lines 17, 18, or 19
      const line20 = Math.min(line17, line18, line19);

      // 6. Calculate Line 21: Taxable Benefits
      const line21 = Math.max(0, line15 - line20);

      let line22;
      let line23;
      let line24;
      let line25;

      if (dependentCare.dependentBenefitsFromOwnBusiness) {
        line22 = dependentCare.dependentBenefitsFromOwnBusinessAmount;
        line23 = line22 - line15;
        line24 = Math.min(line20, line21, line22);
        line25 = line24 - Math.max(0, Math.min(line20, line21));
      } else {
        line22 = 0;
        line24 = Math.min(line20, line21, line22);
        line25 = Math.min(line20, line21);
      }

      line24 = Math.min(line20, line21, line22);

      const line26 = Math.max(0, line25 - line23);

      const limit1 = limits.DEPENDENT_CARE_LIMIT_1;
      const limit2 = limits.DEPENDENT_CARE_LIMIT_2;
      const line27 = qualifyingPersons.length >= 2 ? limit2 : limit1;

      const line28 = line24 + line25;

      const line29 = line28 + dependentCare.form2441_line9b - line27;

      if (line29 <= 0) {
        dependentCare.line29Zero = true;
      } else {
        dependentCare.line29Zero = false;
      }

      let line30;

      line30 = dependentCare.totalExpenses;

      const line31 = Math.min(line30, line29);

      // 7. Update state with all new values if they have changed
      if (line15 !== dependentCare.form2441_line15) onDataChange({ target: { name: 'dependentCare.form2441_line15', value: line15 } });
      if (line17 !== dependentCare.form2441_line17) onDataChange({ target: { name: 'dependentCare.form2441_line17', value: line17 } });
      if (line19 !== dependentCare.form2441_line19) onDataChange({ target: { name: 'dependentCare.form2441_line19', value: line19 } });
      if (line20 !== dependentCare.form2441_line20) onDataChange({ target: { name: 'dependentCare.form2441_line20', value: line20 } });
      if (line21 !== dependentCare.form2441_line21) onDataChange({ target: { name: 'dependentCare.form2441_line21', value: line21 } });
      if (line22 !== dependentCare.form2441_line22) onDataChange({ target: { name: 'dependentCare.form2441_line22', value: line22 } });
      if (line23 !== dependentCare.form2441_line23) onDataChange({ target: { name: 'dependentCare.form2441_line23', value: line23 } });
    }
  }, [
    dependentCare.receivedBenefits,
    dependentCareBenefits,
    dependentCare.priorYearCarryover,
    dependentCare.forfeitedOrCarriedOverAmount,
    dependentCare.dependentBenefitsFromOwnBusiness,
    dependentCare.dependentBenefitsFromOwnBusinessAmount,
    dependentCare.form2441_line9b,
    dependentCare.form2441_line16,
    dependentCare.form2441_line18, // Now depends on user input
    dependentCare.form2441_line19, // Now depends on user input
    filingStatus, // Added filingStatus as a dependency
    onDataChange,
    // Add all dependent properties to avoid stale closures
    dependentCare.form2441_line15,
    dependentCare.form2441_line17,
    dependentCare.form2441_line20,
    dependentCare.form2441_line21,
    dependentCare.form2441_line22,
    dependentCare.form2441_line23,
    dependentCare.totalExpenses,
    limits
  ]);

  return (
    <div className="form-section">
      <div className="content-box-header">
        <h2><Users size={20} className="icon-orange" /> Dependents</h2>
        <div className="header-actions">
          <button onClick={handleAddDependent} className="add-item-button"><PlusCircle size={16} /><span>Add Dependent</span></button>
        </div>
      </div>

      <div className="form-group checkbox-group" style={{ marginBottom: '1.5rem', backgroundColor: '#fff7ed', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fed7aa' }}>
        <input 
          type="checkbox" 
          id="skipDependentSSN" 
          name="skipDependentSSN" 
          checked={formData.skipDependentSSN || false} 
          onChange={onDataChange} 
        />
        <label htmlFor="skipDependentSSN" style={{ color: '#9a3412', fontWeight: '600' }}>
          Skip Dependent SSNs (Fill manually on PDF)
        </label>
        <div style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.85rem', color: '#c2410c', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <Shield size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>
            Checking this will hide SSN inputs for <strong>dependents only</strong>. You must write them on the printed PDF.
          </span>
        </div>
      </div>

      {dependents.length === 0 && <p className="empty-state-message">No dependents added yet.</p>}

      {dependents.map((dep, index) => {
        const qualifications = checkQualifications(dep, taxYear);
        const isChild = CHILD_RELATIONSHIPS.includes(dep.relationship);
        return (
          <React.Fragment key={dep.id}>
            {index === 4 && (
              <div className="supplementary-dependents-note">
                <AlertCircle size={18} />
                <span>Dependents listed below will be included on a supplementary statement with your return.</span>
              </div>
            )}
            <div className="dependent-card">
              <div className="dependent-card-header"><h4>Dependent #{index + 1}</h4><button onClick={() => handleDeleteDependent(dep.id)} className="delete-card-button" title="Remove Dependent"><Trash2 size={18} /></button></div>
              <div className="form-row">
                <div className="form-group"><label>First Name</label><input type="text" value={dep.firstName} onChange={e => handleDependentsChange(dep.id, 'firstName', e.target.value)} /></div>
                <div className="form-group"><label>Last Name</label><input type="text" value={dep.lastName} onChange={e => handleDependentsChange(dep.id, 'lastName', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Social Security Number</label>{formData.skipDependentSSN ? (
                    <input 
                      type="text" 
                      disabled 
                      value="Will be left blank on PDF" 
                      style={{ backgroundColor: '#f6f5f3ff', color: '#afa39cff', fontStyle: 'italic', cursor: 'not-allowed' }} 
                    />
                  ) : (
                    <input 
                      type="text" 
                      placeholder="XXX-XX-XXXX" 
                      value={ssns[dep.id] || dep.ssn || ''} 
                      onChange={e => {
                         handleSsnChange(dep.id, e.target.value);
                         handleDependentsChange(dep.id, 'ssn', e.target.value);
                      }} 
                    />
                  )}</div>
                <div className="form-group"><label>Date of Birth</label><input type="date" value={dep.dob} onChange={e => handleDependentsChange(dep.id, 'dob', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Relationship</label>
                  <select value={dep.relationship} onChange={e => handleDependentsChange(dep.id, 'relationship', e.target.value)}>
                    <option value="" disabled>-- Select --</option>
                    {RELATIONSHIP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="qualification-section">
                <h5>Qualification Details</h5>
                {isChild && (
                  <>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`hasValidTIN-${dep.id}`} checked={dep.hasValidTIN} onChange={e => handleDependentsChange(dep.id, 'hasValidTIN', e.target.checked)} /><label htmlFor={`hasValidTIN-${dep.id}`}>Does this child have a valid SSN, ITIN, or ATIN?</label></div>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`isUSCitizen-${dep.id}`} checked={dep.isUSCitizen} onChange={e => handleDependentsChange(dep.id, 'isUSCitizen', e.target.checked)} /><label htmlFor={`isUSCitizen-${dep.id}`}>Is this child a U.S. citizen, U.S. national, or U.S. resident alien?</label></div>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`wasAdopted-${dep.id}`} checked={dep.wasAdopted} onChange={e => handleDependentsChange(dep.id, 'wasAdopted', e.target.checked)} /><label htmlFor={`wasAdopted-${dep.id}`}>Was this child legally adopted?</label></div>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`disabled-${dep.id}`} checked={dep.isDisabled} onChange={e => handleDependentsChange(dep.id, 'isDisabled', e.target.checked)} /><label htmlFor={`disabled-${dep.id}`}>This person is permanently and totally disabled</label></div>
                  </>
                )}
                {!isChild && (
                  <>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`halfSupport-${dep.id}`} checked={dep.providedHalfSupport} onChange={e => handleDependentsChange(dep.id, 'providedHalfSupport', e.target.checked)} /><label htmlFor={`halfSupport-${dep.id}`}>You provided more than half of this person's support</label></div>
                    <div className="form-group checkbox-group"><input type="checkbox" id={`disabled-${dep.id}`} checked={dep.isDisabled} onChange={e => handleDependentsChange(dep.id, 'isDisabled', e.target.checked)} /><label htmlFor={`disabled-${dep.id}`}>This person is permanently and totally disabled</label></div>
                  </>
                )}
                {dep.relationship === 'Other' && (
                  <div className="form-group checkbox-group"><input type="checkbox" id={`livedWithYou-${dep.id}`} checked={dep.livedWithYou} onChange={e => handleDependentsChange(dep.id, 'livedWithYou', e.target.checked)} /><label htmlFor={`livedWithYou-${dep.id}`}>This person lived with you all year</label></div>
                )}
                {!isChild && (
                  <div className="form-group" style={{marginTop: '1rem'}}><label>Dependent's Gross Income</label><input type="number" value={dep.grossIncome ?? ''} onChange={e => handleDependentsChange(dep.id, 'grossIncome', e.target.value)} /><small>Enter the total income this person earned for the year.</small></div>
                )}
              </div>
              <div className="qualification-feedback">
                {qualifications.isQualifyingChildForCTC && <span className="feedback-tag ctc">Qualifies for Child Tax Credit</span>}
                {qualifications.isQualifyingRelativeForODC && <span className="feedback-tag odc">Qualifies for Credit for Other Dependents</span>}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {showCareProvidersSection && (
        <>
          <div className="student-disabled-section" style={{marginTop: '2rem'}}>
            <div className="content-box-header"><h4>Student or Disability Status</h4></div>
            <p>Answer the following if you paid dependent care expenses, as it may increase your credit amount.</p>
            <div className="form-group checkbox-group"><input type="checkbox" id="userWasStudentOrDisabled" name="dependentCare.userWasStudentOrDisabled" checked={dependentCare.userWasStudentOrDisabled} onChange={onDataChange} /><label htmlFor="userWasStudentOrDisabled">Were <strong>you</strong> a full-time student or disabled during the tax year?</label></div>
            {dependentCare.userWasStudentOrDisabled && (<div className="form-group" style={{marginTop: '1rem'}}><label>Your Gross Income</label><input type="number" name="dependentCare.userEarnedIncome" value={dependentCare.userEarnedIncome ?? ''} onChange={onDataChange} /><small>Enter your total earned income for the year (wages, tips, self-employment).</small></div>)}
            {filingStatus.includes('Married') && (
              <>
                <div className="form-group checkbox-group"><input type="checkbox" id="spouseWasStudentOrDisabled" name="dependentCare.spouseWasStudentOrDisabled" checked={dependentCare.spouseWasStudentOrDisabled} onChange={onDataChange} /><label htmlFor="spouseWasStudentOrDisabled">Was <strong>your spouse</strong> a full-time student or disabled during the tax year?</label></div>
                {dependentCare.spouseWasStudentOrDisabled && (<div className="form-group" style={{marginTop: '1rem'}}><label>Spouse's Gross Income</label><input type="number" name="dependentCare.spouseEarnedIncome" value={dependentCare.spouseEarnedIncome ?? ''} onChange={onDataChange} /><small>Enter your spouse's total earned income for the year (wages, tips, self-employment).</small></div>)}
              </>
            )}
          </div>

          <div className="care-providers-section">
            <div className="content-box-header"><h2><Heart size={20} className="icon-orange" /> Care Providers</h2><div className="header-actions"><button onClick={handleAddProvider} className="add-item-button"><PlusCircle size={16} /><span>Add Provider</span></button></div></div>
            <p>Add each person or organization you paid for the care of your qualifying dependent(s).</p>
            {careProviders.length === 0 && <p className="empty-state-message">No care providers added yet.</p>}
            {careProviders.map((provider, index) => (
              <div key={provider.id} className="dependent-card">
                <div className="dependent-card-header"><h4>Care Provider #{index + 1}</h4><button onClick={() => handleDeleteProvider(provider.id)} className="delete-card-button" title="Remove Provider"><Trash2 size={18} /></button></div>
                <div className="form-row"><div className="form-group"><label>Provider's Name</label><input type="text" value={provider.name} onChange={e => handleProviderChange(index, 'name', e.target.value)} /></div></div>
                <div className="form-group"><label>Provider's Address</label><input type="text" placeholder="Street Address" value={provider.address.street} onChange={e => handleProviderChange(index, 'address.street', e.target.value)} style={{ marginBottom: '0.5rem' }} /><div className="form-row"><input type="text" placeholder="City" value={provider.address.city} onChange={e => handleProviderChange(index, 'address.city', e.target.value)} /><input type="text" placeholder="State" value={provider.address.state} onChange={e => handleProviderChange(index, 'address.state', e.target.value)} /><input type="text" placeholder="ZIP Code" value={provider.address.zip} onChange={e => handleProviderChange(index, 'address.zip', e.target.value)} /></div></div>
                <div className="form-row"><div className="form-group"><label>Provider's SSN or EIN</label><input type="text" value={provider.tin} onChange={e => handleProviderChange(index, 'tin', e.target.value)} /></div><div className="form-group"><label>Amount Paid</label><input type="number" value={provider.amountPaid ?? ''} onChange={e => handleProviderChange(index, 'amountPaid', e.target.value)} /></div></div>
                <div className="form-group checkbox-group"><input type="checkbox" id={`isEmployee-${provider.id}`} checked={provider.isEmployee} onChange={e => handleProviderChange(index, 'isEmployee', e.target.checked)} /><label htmlFor={`isEmployee-${provider.id}`}>Was this provider your household employee (e.g., a nanny)?</label></div>
              </div>
            ))}
          </div>
          
          <div className="form-section" style={{marginTop: '2rem'}}>
            <div className="content-box-header"><h2><DollarSign size={20} className="icon-orange" /> Dependent Care Benefits</h2></div>
            <div className="form-group">
              <label>Did you receive dependent care benefits in {taxYear}?</label>
              <div className="radio-group">
                <input 
                  type="radio" 
                  id="benefitsNo" 
                  name="dependentCare.receivedBenefits" 
                  value="false" // Use string "false"
                  checked={dependentCare.receivedBenefits === false} 
                  onChange={handleBenefitsRadioChange} // Use the specific handler
                />
                <label htmlFor="benefitsNo">No</label>
                
                <input 
                  type="radio" 
                  id="benefitsYes" 
                  name="dependentCare.receivedBenefits" 
                  value="true" // Use string "true"
                  checked={dependentCare.receivedBenefits === true} 
                  onChange={handleBenefitsRadioChange} // Use the specific handler
                />
                <label htmlFor="benefitsYes">Yes</label>
              </div>
              <small>These are benefits provided by your employer, often shown in Box 10 of your W-2.</small>
            </div>

            {!dependentCare.receivedBenefits && (
              <div className="qualifying-persons-list">
                <h4 style={{marginTop: '1.5rem'}}>Qualifying Persons & Expenses ({taxYear})</h4>
                <p>Enter the total expenses you paid in {taxYear} for each qualifying person.</p>
                {qualifyingPersons.length === 0 && (<p className="empty-state-message">No qualifying persons found. Add a dependent under age 13 or one who is disabled to enter care expenses.</p>)}
                {qualifyingPersons.map((person) => (
                  <div key={person.id} className="dependent-card">
                    <div className="form-row">
                      <div className="form-group" style={{flexGrow: 2}}><label>Qualifying Person</label><input type="text" value={`${person.firstName} ${person.lastName}`} readOnly style={{ backgroundColor: '#faf9f8ff' }} /></div>
                      <div className="form-group"><label>Total Expenses Paid</label><input type="number" value={person.qualifiedCareExpenses ?? ''} onChange={e => handleDependentsChange(person.id, 'qualifiedCareExpenses', e.target.value)} /></div>
                    </div>
                  </div>
                ))}
                <div className="totals-summary-box" style={{marginTop: '1.5rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem'}}>
                  <div className="form-group"><label>Total Qualifying Expenses to be Used for Credit</label><input type="number" value={dependentCare.totalExpenses ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }} /><small>This total is automatically calculated from your entries and is capped at the IRS limit of ${qualifyingPersons.length >= 2 ? '6,000' : '3,000'}.</small></div>
                </div>
                
                <div className="form-section" style={{marginTop: '1.5rem'}}>
                   {/* TODO: Add earned income comparison for the taxpayer (and spouse if MFJ) to determine the final expense limit for the credit calculation. */}
                  <div className="form-group checkbox-group">
                    <input type="checkbox" id="paidForPriorYearCare" name="dependentCare.paidForPriorYearCare" checked={dependentCare.paidForPriorYearCare} onChange={onDataChange} />
                    <label htmlFor="paidForPriorYearCare">I paid for care in {taxYear} that was for services provided in {taxYear - 1}</label>
                  </div>

                  {dependentCare.paidForPriorYearCare && (
                    <div className="dependent-card" style={{marginTop: '1rem'}}>
                       <h5 style={{marginBottom: '1rem'}}>Prior Year ({taxYear - 1}) Care Expenses</h5>
                       <div className="form-row">
                          <div className="form-group">
                              <label>How many qualifying persons did you have in {taxYear - 1}?</label>
                              <input type="number" name="dependentCare.priorYearQualifyingPersonsCount" value={dependentCare.priorYearQualifyingPersonsCount ?? ''} onChange={onDataChange} />
                          </div>
                           <div className="form-group">
                              <label>Expenses from {taxYear - 1} paid in {taxYear}</label>
                              <input type="number" value={dependentCare.priorYearExpensesPaidInCurrentYear ?? ''} onChange={handlePriorYearExpenseChange} />
                              <small>Amount is capped at ${dependentCare.priorYearQualifyingPersonsCount >= 2 ? '6,000' : '3,000'}.</small>
                          </div>
                       </div>
                       <div className="form-group" style={{marginTop: '1rem'}}>
                          <div className="header-title-with-help">
                              <label>Benefits Received in {taxYear - 1} (Net)</label>
                              <div className="has-tooltip header-help-icon" data-tooltip={`From your ${taxYear - 1} return, find Form 2441. Enter the amount of dependent care benefits you received, then subtract any amount you were able to exclude or deduct on that return.`}>
                                  <HelpCircle size={18} />
                              </div>
                          </div>
                          <input type="number" name="dependentCare.priorYearBenefitsReceivedLessExclusions" value={dependentCare.priorYearBenefitsReceivedLessExclusions ?? ''} onChange={onDataChange} />
                       </div>
                       <div className="form-row" style={{marginTop: '1rem'}}>
                          <div className="form-group">
                            <label>Your earned income in {taxYear - 1}</label>
                            <input type="number" name="dependentCare.priorYearUserEarnedIncome" value={dependentCare.priorYearUserEarnedIncome ?? ''} onChange={onDataChange} />
                          </div>
                          {filingStatus.includes('Married') && (
                             <div className="form-group">
                                <label>Spouse's earned income in {taxYear - 1}</label>
                                <input type="number" name="dependentCare.priorYearSpouseEarnedIncome" value={dependentCare.priorYearSpouseEarnedIncome ?? ''} onChange={onDataChange} />
                             </div>
                          )}
                       </div>
                       <div className="totals-summary-box" style={{marginTop: '1.5rem'}}>
                         <div className="form-group">
                            <label>Worksheet A, Line 10 Amount</label>
                            <input type="number" value={dependentCare.worksheetA10 ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }} />
                            <small>This is the base amount calculated for your credit.</small>
                         </div>
                       </div>
                       
                       {dependentCare.worksheetA10 > 0 && (
                          <div className="prior-year-credit-finalization" style={{marginTop: '1.5rem'}}>
                              <div className="form-group">
                                  <label>Your Adjusted Gross Income (AGI) from {taxYear - 1}</label>
                                  <input type="number" name="dependentCare.priorYearAGI" value={dependentCare.priorYearAGI ?? ''} onChange={onDataChange}/>
                                  <small>Enter the AGI from your {taxYear-1} tax return (e.g., Form 1040, line 11).</small>
                              </div>
                              <div className="totals-summary-box" style={{marginTop: '1.5rem'}}>
                                  <div className="form-group">
                                      <label>Credit for {taxYear - 1} Expenses (Form 2441, Line 9b)</label>
                                      <input type="number" value={dependentCare.form2441_line9b ? dependentCare.form2441_line9b.toFixed(2) : '0.00'} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }}/>
                                      <small>This calculated amount will be added to your credit on this year's return.</small>
                                  </div>
                              </div>
                              <div className="info-section" style={{marginTop: '2rem'}}>
                                  <h5 style={{borderTop: '1px solid var(--border-color)', paddingTop: '1rem'}}>Information for Your Return</h5>
                                  <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem'}}>Please have the names, addresses, and tax ID numbers for your {taxYear-1} care providers and qualifying persons available, as they must be listed on Form 2441.</p>
                              </div>
                          </div>
                       )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {dependentCare.receivedBenefits && (
              <div className="benefits-received-list">
                <div className="content-box-header" style={{marginTop: '1.5rem'}}><h4>List of Benefits Received</h4><div className="header-actions"><button onClick={handleAddBenefit} className="add-item-button"><PlusCircle size={16} /><span>Add Benefit</span></button></div></div>
                {dependentCareBenefits.map((benefit, index) => (
                  <div key={benefit.id} className="dependent-card">
                    <div className="dependent-card-header"><h4>Benefit #{index + 1}</h4><button onClick={() => handleDeleteBenefit(benefit.id)} className="delete-card-button"><Trash2 size={18} /></button></div>
                    <div className="form-row"><div className="form-group"><label>Description</label><input type="text" value={benefit.description} onChange={e => handleBenefitChange(index, 'description', e.target.value)} /></div><div className="form-group"><label>Amount</label><input type="number" value={benefit.amount ?? ''} onChange={e => handleBenefitChange(index, 'amount', e.target.value)} /></div></div>
                  </div>
                ))}
                <div className="form-group" style={{marginTop: '1.5rem'}}>
                  <div className="header-title-with-help"><label htmlFor="priorYearCarryover">Exclusion and Rollover from Prior Year</label><div className="has-tooltip header-help-icon" data-tooltip="Enter any amount from last year's Form 2441 that you were unable to exclude, which you are now carrying over to this year."><HelpCircle size={18} /></div></div>
                  <input type="number" id="priorYearCarryover" name="dependentCare.priorYearCarryover" value={dependentCare.priorYearCarryover ?? ''} onChange={onDataChange} />
                </div>
                <div className="form-group" style={{marginTop: '1.5rem'}}>
                  <div className="header-title-with-help"><label htmlFor="forfeitedOrCarriedOverAmount">Forfeited Benefits or Carryover to {taxYear + 1}</label><div className="has-tooltip header-help-icon" data-tooltip="This often applies to Flexible Spending Accounts (FSAs). Enter any amount you were unable to use by the deadline and had to forfeit, OR any amount you are permitted to carry over to use next year."><HelpCircle size={18} /></div></div>
                  <input type="number" id="forfeitedOrCarriedOverAmount" name="dependentCare.forfeitedOrCarriedOverAmount" value={dependentCare.forfeitedOrCarriedOverAmount ?? ''} onChange={onDataChange} />
                </div>
                <div className="dependent-card" style={{marginTop: '2rem'}}>
                  <h5 style={{marginBottom: '1rem'}}>Taxable Benefits Calculation (Form 2441, Part III)</h5>
                  <div className="form-group">
                    <label>Line 15: Adjusted Dependent Care Benefits</label>
                    <input type="number" value={dependentCare.form2441_line15 ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff' }} />
                    <small>This is the total benefits received, adjusted for any carryovers or forfeited amounts.</small>
                  </div>
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Line 16: Total qualified expenses for all dependents in {taxYear}</label>
                    <input 
                      type="number" 
                      name="dependentCare.form2441_line16"
                      value={dependentCare.form2441_line16 ?? ''}
                      onChange={onDataChange}
                    />
                  </div>
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Line 17: Smallest of Line 15 or Line 16</label>
                    <input type="number" value={dependentCare.form2441_line17 ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff' }} />
                  </div>
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Line 18: Your Earned Income</label>
                    <input 
                      type="number"
                      name="dependentCare.form2441_line18"
                      value={dependentCare.form2441_line18 ?? ''}
                      onChange={onDataChange}
                    />
                    <small>Enter the total of your W-2 wages and any net profit from self-employment.</small>
                  </div>
                  {filingStatus.includes('Married') && (
                    <div className="form-group" style={{marginTop: '1rem'}}>
                      <label>Line 19: Spouse's Earned Income</label>
                      <input 
                        type="number"
                        name="dependentCare.form2441_line19"
                        value={dependentCare.form2441_line19 ?? ''}
                        onChange={onDataChange}
                      />
                      <small>Enter the total of your spouse's W-2 wages and any net profit from self-employment.</small>
                    </div>
                  )}
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Line 20: Smallest of Line 17, 18, or 19</label>
                    <input type="number" value={dependentCare.form2441_line20 ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff' }} />
                  </div>
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Line 21: Taxable Dependent Care Benefits</label>
                    <input type="number" value={dependentCare.form2441_line21 ?? ''} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    <small>This amount may be included in your taxable income.</small>
                  </div>
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Were any benefits provided by your own business?</label>
                    <div className="radio-group">
                      <input 
                        type="radio" 
                        id="benefitsFromBusinessNo" 
                        name="dependentCare.dependentBenefitsFromOwnBusiness" 
                        value="false" 
                        checked={dependentCare.dependentBenefitsFromOwnBusiness === false} 
                        onChange={handleBenefitsRadioChange} // Use the existing, correct handler
                      />
                      <label htmlFor="benefitsFromBusinessNo">No</label>
                      
                      <input 
                        type="radio" 
                        id="benefitsFromBusinessYes" 
                        name="dependentCare.dependentBenefitsFromOwnBusiness" 
                        value="true" 
                        checked={dependentCare.dependentBenefitsFromOwnBusiness === true} 
                        onChange={handleBenefitsRadioChange} // Use the existing, correct handler
                      />
                      <label htmlFor="benefitsFromBusinessYes">Yes</label>
                    </div>
                    <small>Answer "Yes" if you were self-employed and the benefits are from your sole proprietorship or partnership.</small>
                  </div>
                  {dependentCare.dependentBenefitsFromOwnBusiness && (
                    <div className="form-group">
                      <label style={{ marginTop: "1rem" }}>Enter the amount of benefits from your own business</label>
                      <input
                        type="number"
                        value={dependentCare.dependentBenefitsFromOwnBusinessAmount ?? ''}
                        style={{ backgroundColor: "#faf9f8ff" }}
                        onChange={onDataChange} />
                    </div>
                  )}
                  {dependentCare.receivedBenefits && (
                    <div className="qualifying-persons-list">
                      <h4 style={{marginTop: '1.5rem'}}>Qualifying Persons & Expenses ({taxYear})</h4>
                      <p>Enter the total expenses you paid in {taxYear} for each qualifying person.</p>
                      {qualifyingPersons.length === 0 && (<p className="empty-state-message">No qualifying persons found. Add a dependent under age 13 or one who is disabled to enter care expenses.</p>)}
                      {qualifyingPersons.map((person) => (
                        <div key={person.id} className="dependent-card">
                          <div className="form-row">
                            <div className="form-group" style={{flexGrow: 2}}><label>Qualifying Person</label><input type="text" value={`${person.firstName} ${person.lastName}`} readOnly style={{ backgroundColor: '#faf9f8ff' }} /></div>
                            <div className="form-group"><label>Total Expenses Paid</label><input type="number" value={person.qualifiedCareExpenses ?? ''} onChange={e => handleDependentsChange(person.id, 'qualifiedCareExpenses', e.target.value)} /></div>
                          </div>
                        </div>
                      ))}
                      <div className="totals-summary-box" style={{marginTop: '1.5rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem'}}>
                        <div className="form-group"><label>Total Qualifying Expenses to be Used for Credit</label><input type="number" value={dependentCare.totalExpenses ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }} /><small>This total is automatically calculated from your entries and is capped at the IRS limit of ${qualifyingPersons.length >= 2 ? '6,000' : '3,000'}.</small></div>
                      </div>
                      
                      <div className="form-section" style={{marginTop: '1.5rem'}}>
                        {/* TODO: Add earned income comparison for the taxpayer (and spouse if MFJ) to determine the final expense limit for the credit calculation. */}
                        <div className="form-group checkbox-group">
                          <input type="checkbox" id="paidForPriorYearCare" name="dependentCare.paidForPriorYearCare" checked={dependentCare.paidForPriorYearCare} onChange={onDataChange} />
                          <label htmlFor="paidForPriorYearCare">I paid for care in {taxYear} that was for services provided in {taxYear - 1}</label>
                        </div>

                        {dependentCare.paidForPriorYearCare && (
                          <div className="dependent-card" style={{marginTop: '1rem'}}>
                            <h5 style={{marginBottom: '1rem'}}>Prior Year ({taxYear - 1}) Care Expenses</h5>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>How many qualifying persons did you have in {taxYear - 1}?</label>
                                    <input type="number" name="dependentCare.priorYearQualifyingPersonsCount" value={dependentCare.priorYearQualifyingPersonsCount ?? ''} onChange={onDataChange} />
                                </div>
                                <div className="form-group">
                                    <label>Expenses from {taxYear - 1} paid in {taxYear}</label>
                                    <input type="number" value={dependentCare.priorYearExpensesPaidInCurrentYear ?? ''} onChange={handlePriorYearExpenseChange} />
                                    <small>Amount is capped at ${dependentCare.priorYearQualifyingPersonsCount >= 2 ? '6,000' : '3,000'}.</small>
                                </div>
                            </div>
                            <div className="form-group" style={{marginTop: '1rem'}}>
                                <div className="header-title-with-help">
                                    <label>Benefits Received in {taxYear - 1} (Net)</label>
                                    <div className="has-tooltip header-help-icon" data-tooltip={`From your ${taxYear - 1} return, find Form 2441. Enter the amount of dependent care benefits you received, then subtract any amount you were able to exclude or deduct on that return.`}>
                                        <HelpCircle size={18} />
                                    </div>
                                </div>
                                <input type="number" name="dependentCare.priorYearBenefitsReceivedLessExclusions" value={dependentCare.priorYearBenefitsReceivedLessExclusions ?? ''} onChange={onDataChange} />
                            </div>
                            <div className="form-row" style={{marginTop: '1rem'}}>
                                <div className="form-group">
                                  <label>Your earned income in {taxYear - 1}</label>
                                  <input type="number" name="dependentCare.priorYearUserEarnedIncome" value={dependentCare.priorYearUserEarnedIncome ?? ''} onChange={onDataChange} />
                                </div>
                                {filingStatus.includes('Married') && (
                                  <div className="form-group">
                                      <label>Spouse's earned income in {taxYear - 1}</label>
                                      <input type="number" name="dependentCare.priorYearSpouseEarnedIncome" value={dependentCare.priorYearSpouseEarnedIncome ?? ''} onChange={onDataChange} />
                                  </div>
                                )}
                            </div>
                            <div className="totals-summary-box" style={{marginTop: '1.5rem'}}>
                              <div className="form-group">
                                  <label>Worksheet A, Line 10 Amount</label>
                                  <input type="number" value={dependentCare.worksheetA10 ?? ''} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }} />
                                  <small>This is the base amount calculated for your credit.</small>
                              </div>
                            </div>
                            
                            {dependentCare.worksheetA10 > 0 && (
                                <div className="prior-year-credit-finalization" style={{marginTop: '1.5rem'}}>
                                    <div className="form-group">
                                        <label>Your Adjusted Gross Income (AGI) from {taxYear - 1}</label>
                                        <input type="number" name="dependentCare.priorYearAGI" value={dependentCare.priorYearAGI ?? ''} onChange={onDataChange}/>
                                        <small>Enter the AGI from your {taxYear-1} tax return (e.g., Form 1040, line 11).</small>
                                    </div>
                                    <div className="totals-summary-box" style={{marginTop: '1.5rem'}}>
                                        <div className="form-group">
                                            <label>Credit for {taxYear - 1} Expenses (Form 2441, Line 9b)</label>
                                            <input type="number" value={dependentCare.form2441_line9b ? dependentCare.form2441_line9b.toFixed(2) : '0.00'} readOnly style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }}/>
                                            <small>This calculated amount will be added to your credit on this year's return.</small>
                                        </div>
                                    </div>
                                    <div className="info-section" style={{marginTop: '2rem'}}>
                                        <h5 style={{borderTop: '1px solid var(--border-color)', paddingTop: '1rem'}}>Information for Your Return</h5>
                                        <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem'}}>Please have the names, addresses, and tax ID numbers for your {taxYear-1} care providers and qualifying persons available, as they must be listed on Form 2441.</p>
                                    </div>
                                </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {adoptedQualifyingDependents.length > 0 && (
        <div className="adoption-details-section" style={{marginTop: '2rem'}}>
          <div className="content-box-header">
            <h2><Star size={20} className="icon-orange" /> Adoption Details</h2>
          </div>
          <p>Please provide the following details for each adopted child listed.</p>
          {adoptedQualifyingDependents.map(dep => (
            <div key={dep.id} className="dependent-card">
              <div className="form-group">
                <label>Adopted Child</label>
                <input 
                  type="text" 
                  value={`${dep.firstName} ${dep.lastName}`} 
                  readOnly 
                  style={{ backgroundColor: '#faf9f8ff' }} 
                />
              </div>
              <div className="form-group checkbox-group" style={{marginTop: '1rem'}}>
                <input 
                  type="checkbox" 
                  id={`isForeignChild-${dep.id}`} 
                  checked={dep.isForeignChild || false} 
                  onChange={e => handleDependentsChange(dep.id, 'isForeignChild', e.target.checked)} 
                />
                <label htmlFor={`isForeignChild-${dep.id}`}>Is this a foreign child?</label>
              </div>
              <div className="form-group checkbox-group" style={{marginTop: '1rem'}}>
                <input 
                  type="checkbox" 
                  id={`adoptionFinalized-${dep.id}`} 
                  checked={dep.adoptionFinalizedInYear || false} 
                  onChange={e => handleDependentsChange(dep.id, 'adoptionFinalizedInYear', e.target.checked)} 
                />
                <label htmlFor={`adoptionFinalized-${dep.id}`}>Was the adoption finalized in {taxYear}?</label>
              </div>
              <div className="form-group" style={{marginTop: '1rem'}}>
                <label>Enter the total amount of qualified adoption expenses for this child.</label>
                <input
                  type="number"
                  name={`adoptionCredit.expensesByChild.${dep.id}`}
                  value={adoptionCredit.expensesByChild?.[dep.id] ?? ''}
                  onChange={onDataChange} />
              </div>
            </div>
          ))}
          <div className="form-group" style={{marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem'}}>
            <label>Did you file for adoption credits in the previous year ({taxYear - 1})</label>
            <div className="radio-group">
              <input
                type="radio"
                id="adoptionCreditsPrevYearRadioNo"
                name="adoptionCredit.adoptedCreditsPrevYear"
                value="false"
                checked={adoptionCredit.adoptionCreditsPrevYear === false}
                onChange={handleBenefitsRadioChange} />
              <label htmlFor="adoptionCreditsPrevYearRadioNo">No</label>
              <input
                type="radio"
                id="adoptionCreditsPrevYearRadioYes"
                name="adoptionCredit.adoptedCreditsPrevYear"
                value="true"
                checked={adoptionCredit.adoptionCreditsPrevYear === true}
                onChange={handleBenefitsRadioChange} />
              <label htmlFor="adoptionCreditsPrevYearRadioYes">Yes</label>
            </div>
          </div>
          {adoptionCredit.adoptionCreditsPrevYear && (
            <div className="form-group">
              <label>Enter the total of the amounts shown on lines 3 and 6 of the previous form 8839</label>
              <input
                type="number"
                name="adoptionCredit.form8839_year2024_line3"
                value={adoptionCredit.form8839_year2024_line3 ?? ''}
                onChange={onDataChange} />
            </div>
          )}
          <small style={{marginTop: '0.5rem', lineHeight: '1.5'}}>
            <b>Note:</b> You may need to add extra amounts to your modified AGI. See 8839 Line 7 instructions at the irs.gov website and add to the provided form 8839 on Line 7.
          </small>
          
          <div className="form-group" style={{marginTop: '1.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem'}}>
            <label>Did you have an adoption credit carryforward from {taxYear - 1}?</label>
            <div className="radio-group">
              <input
                type="radio"
                id="adoptionCarryforwardNo"
                name="adoptionCredit.hasCarryforward"
                value="false"
                checked={adoptionCredit.hasCarryforward === false}
                onChange={handleBenefitsRadioChange} />
              <label htmlFor="adoptionCarryforwardNo">No</label>
              <input
                type="radio"
                id="adoptionCarryforwardYes"
                name="adoptionCredit.hasCarryforward"
                value="true"
                checked={adoptionCredit.hasCarryforward === true}
                onChange={handleBenefitsRadioChange} />
              <label htmlFor="adoptionCarryforwardYes">Yes</label>
            </div>
          </div>

          {adoptionCredit.hasCarryforward && (
            <div className="dependent-card" style={{marginTop: '1rem'}}>
              <h5 style={{marginBottom: '1rem'}}>Adoption Credit Carryforward Worksheet</h5>
              <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.5rem'}}>
                Complete this section to calculate your total adoption credit carryforward to {taxYear + 1}.
              </p>

              <div className="form-group">
                <label>Credit from Form 8839, Line 12 ({taxYear} Credit)</label>
                <input
                  type="number"
                  name="adoptionCredit.carryforward_line1"
                  value={adoptionCredit.carryforward_line1 ?? ''}
                  onChange={onDataChange} />
                <small>Enter the amount from your {taxYear} Form 8839, line 12.</small>
              </div>

              <div className="form-group checkbox-group" style={{marginTop: '1.5rem'}}>
                <input
                  type="checkbox"
                  id="usedWorksheetIn2023"
                  name="adoptionCredit.usedWorksheetIn2023"
                  checked={adoptionCredit.usedWorksheetIn2023 || false}
                  onChange={onDataChange} />
                <label htmlFor="usedWorksheetIn2023">I used the Adoption Credit Carryforward Worksheet in {taxYear - 1}</label>
              </div>

              {adoptionCredit.usedWorksheetIn2023 && (
                <div style={{marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fbfaf9ff', border: '1px solid var(--border-color)', borderRadius: '0.5rem'}}>
                  <h5 style={{marginBottom: '1rem', fontSize: '0.95rem'}}>Prior Year Carryforwards</h5>
                  <p style={{color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1rem'}}>
                    Enter the carryforward amounts from your {taxYear - 1} worksheet.
                  </p>

                  <div className="form-group">
                    <label>{taxYear - 5} Credit Carryforward <span style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>(line 12 of your {taxYear - 1} worksheet)</span></label>
                    <input
                      type="number"
                      name="adoptionCredit.carryforward_2019"
                      value={adoptionCredit.carryforward_2019 ?? ''}
                      onChange={onDataChange} />
                  </div>

                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>{taxYear - 4} Credit Carryforward <span style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>(line 14 of your {taxYear - 1} worksheet)</span></label>
                    <input
                      type="number"
                      name="adoptionCredit.carryforward_2020"
                      value={adoptionCredit.carryforward_2020 ?? ''}
                      onChange={onDataChange} />
                  </div>

                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>{taxYear - 3} Credit Carryforward <span style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>(line 16 of your {taxYear - 1} worksheet)</span></label>
                    <input
                      type="number"
                      name="adoptionCredit.carryforward_2021"
                      value={adoptionCredit.carryforward_2021 ?? ''}
                      onChange={onDataChange} />
                  </div>

                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>{taxYear - 2} Credit Carryforward <span style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>(line 18 of your {taxYear - 1} worksheet)</span></label>
                    <input
                      type="number"
                      name="adoptionCredit.carryforward_2022"
                      value={adoptionCredit.carryforward_2022 ?? ''}
                      onChange={onDataChange} />
                  </div>

                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>{taxYear - 1} Credit Carryforward <span style={{color: 'var(--text-secondary)', fontSize: '0.875rem'}}>(line 20 of your {taxYear - 1} worksheet)</span></label>
                    <input
                      type="number"
                      name="adoptionCredit.carryforward_2023"
                      value={adoptionCredit.carryforward_2023 ?? ''}
                      onChange={onDataChange} />
                  </div>
                </div>
              )}

              <div className="form-group" style={{marginTop: '1.5rem'}}>
                <label>Credit Used in {taxYear} (Form 8839, Line 16)</label>
                <input
                  type="number"
                  name="adoptionCredit.carryforward_line16"
                  value={adoptionCredit.carryforward_line16 ?? ''}
                  onChange={onDataChange} />
                <small>Enter the amount from your {taxYear} Form 8839, line 16.</small>
              </div>

              <div className="totals-summary-box" style={{marginTop: '1.5rem'}}>
                <div className="form-group">
                  <label>Total Credit Carryforward to {taxYear + 1}</label>
                  <input
                    type="number"
                    value={adoptionCredit.totalCarryforward ?? ''}
                    readOnly
                    style={{ backgroundColor: '#faf9f8ff', cursor: 'not-allowed' }} />
                  <small>This calculated amount will be carried forward to your {taxYear + 1} tax return.</small>
                </div>
              </div>
            </div>
          )}
          <div className="dependent-card" style={{ marginTop: '1.5rem', border: '1px solid #e1d7cbff', backgroundColor: '#fcfaf8ff' }}>
            <div className="content-box-header" style={{ marginBottom: '1rem' }}>
              <div className="header-title-with-help">
                <h4><FileText size={18} className="icon-orange" /> Credit Limit Worksheet (Form 8839, Line 15)</h4>
                <div className="has-tooltip header-help-icon" data-tooltip="This worksheet calculates the limit on your adoption credit based on your tax liability.">
                  <HelpCircle size={16} />
                </div>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>1. Total Available Credit (Form 8839, Line 14)</label>
                <input type="number" value={adoptionWorksheet?.line1 ?? ''} readOnly style={{ backgroundColor: '#f0e7e2ff' }} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>2. Total Tax Liability (Form 1040, Line 18)</label>
                <input type="number" value={adoptionWorksheet?.line2 ?? ''} readOnly style={{ backgroundColor: '#f0e8e2ff' }} />
              </div>
              <div className="form-group">
                <label>3. Other Credits (Line 19 + Schedule 3)</label>
                <input type="number" value={adoptionWorksheet?.line3 ?? ''} readOnly style={{ backgroundColor: '#f0e9e2ff' }} />
                <small>Includes Child Tax Credit, Dependent Care Credit, etc.</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>4. Tax Liability Limit (Line 2 minus Line 3)</label>
                <input type="number" value={adoptionWorksheet?.line4 ?? ''} readOnly style={{ backgroundColor: '#f0e8e2ff', fontWeight: 'bold' }} />
              </div>
            </div>

            <div className="totals-summary-box" style={{ marginTop: '1rem', backgroundColor: '#fff', border: '2px solid #f0e9e2ff' }}>
              <div className="form-group">
                <label style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>5. Credit Allowed for Current Year (Smaller of Line 1 or 4)</label>
                <input type="number" value={adoptionWorksheet?.line5 ?? ''} readOnly style={{ backgroundColor: '#fff', fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)' }} />
                <small>This is the amount entered on Form 8839, Line 15.</small>
              </div>
            </div>
          </div>
          <div className="form-group" style={{marginTop: '1.5rem'}}>
            <label>Did you receive employer-provided adoption benefits in {taxYear}?</label>
            <div className="radio-group">
              <input
                type="radio"
                id="employerBenefitsNo"
                name="adoptionCredit.receivedEmployerBenefits"
                value="false"
                checked={adoptionCredit.receivedEmployerBenefits === false}
                onChange={handleBenefitsRadioChange} 
              />
              <label htmlFor="employerBenefitsNo">No</label>
              <input
                type="radio"
                id="employerBenefitsYes"
                name="adoptionCredit.receivedEmployerBenefits"
                value="true"
                checked={adoptionCredit.receivedEmployerBenefits === true}
                onChange={handleBenefitsRadioChange} 
              />
              <label htmlFor="employerBenefitsYes">Yes</label>
            </div>
            <small>This is often reported in Box 12 of your W-2 with code T.</small>
          </div>
        </div>
      )}

      {adoptionCredit.receivedEmployerBenefits && (
        <div className="benefits-by-child-section" style={{marginTop: '1rem'}}>
          <h5 style={{marginBottom: '1rem'}}>Employer-Provided Benefits by Child</h5>
          <p style={{color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem'}}>
            Enter the amount of benefits you received from your employer for each specific child.
          </p>
          {adoptedQualifyingDependents.map(dep => {
            // Get the specific benefit data for this child, or an empty object if it doesn't exist yet
            const childBenefits = adoptionCredit.benefitsByChild?.[dep.id] || {};
            return (
              <div key={dep.id} className="dependent-card">
                <div className="form-group">
                  <label>Adopted Child</label>
                  <input type="text" value={`${dep.firstName} ${dep.lastName}`} readOnly style={{ backgroundColor: '#faf9f8ff' }} />
                </div>
                <div className="form-group" style={{marginTop: '1rem'}}>
                  <label>Benefits received for this child in {taxYear}</label>
                  <input
                    type="number"
                    value={childBenefits.currentYear ?? ''}
                    onChange={e => handleAdoptionBenefitChange(dep.id, 'currentYear', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="form-group checkbox-group" style={{marginTop: '1rem'}}>
                  <input
                    type="checkbox"
                    id={`priorYearBenefits-${dep.id}`}
                    checked={childBenefits.receivedPriorYear || false}
                    onChange={e => handleAdoptionBenefitChange(dep.id, 'receivedPriorYear', e.target.checked)}
                  />
                  <label htmlFor={`priorYearBenefits-${dep.id}`}>Received benefits for this child in {taxYear - 1}?</label>
                </div>
                {childBenefits.receivedPriorYear && (
                  <div className="form-group" style={{marginTop: '1rem'}}>
                    <label>Benefits received for this child in {taxYear - 1}</label>
                    <input
                      type="number"
                      value={childBenefits.priorYear ?? ''}
                      onChange={e => handleAdoptionBenefitChange(dep.id, 'priorYear', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                )}
                <div className="form-group" style={{marginTop: '1rem'}}>
                  <label>Benefits received for this child in {taxYear}</label>
                  <input
                    type="number"
                    value={childBenefits.currentYear ?? ''}
                    onChange={e => handleAdoptionBenefitChange(dep.id, 'currentYear', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DependentsForm;
